import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useIsFirstAuthAttempt } from '../../../context/AuthMachine';
import { useNavigationReset } from '../../../routing/hooks/useNavigationReset';

// ─────────────────────────────────────────────────────────────────────────────

export const useRedirectAfterAuthStateChange = (shouldReplace?: boolean) => {
  const router = useRouter();
  const resetNavigation = useNavigationReset();
  const isFirstAuthAttempt = useIsFirstAuthAttempt();

  return useCallback(
    (href: string | undefined) => {
      const target = checkIfSafeInternalHref(href) ? href : '/';

      const navigateToTarget = () => {
        if (shouldReplace) {
          router.replace(target as never);
          return;
        }

        router.navigate(target as never);
      };

      /**
       * Mid-session login (signed-out → signed-in): clear the auth screens
       * out of the stack first so a back gesture won't return to them.
       *
       * NOTE: Cold start has no stale stack to clear, so we navigate immediately.
       */
      if (!isFirstAuthAttempt) {
        resetNavigation({
          index: 0,
          routes: [{ name: '(tabs)', state: { routes: [{ name: '(home)' }] } }],
        });

        // NOTE: We use a timeout to ensure we navigate only after the reset has committed.
        setTimeout(navigateToTarget, RESET_NAVIGATION_DELAY_MS);

        return;
      }

      navigateToTarget();
    },
    [router, shouldReplace, resetNavigation, isFirstAuthAttempt],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfSafeInternalHref(href: string | undefined): href is string {
  if (typeof href !== 'string' || href.length === 0) return false;

  // Must start with a single `/` — `//` is a protocol-relative URL.
  return href.startsWith('/') && !href.startsWith('//');
}

// ─── Constants ───────────────────────────────────────────────────────────────

const RESET_NAVIGATION_DELAY_MS = 500;
